/**
 * Clipboard utilities for encoder tools
 * Supports modern clipboard API with fallbacks
 */

export class ClipboardUtils {
  /**
   * Copy text to clipboard with fallback support
   */
  static async copyText(text: string): Promise<boolean> {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      // Fall back to execCommand if clipboard API fails
    }

    try {
      // Fallback for older browsers
      return this.fallbackCopy(text);
    } catch (error) {
      console.error('Failed to copy text:', error);
      return false;
    }
  }

  /**
   * Read text from clipboard
   */
  static async readText(): Promise<string | null> {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        return await navigator.clipboard.readText();
      }
    } catch (error) {
      // Clipboard read may not be available or permitted
    }

    return null;
  }

  /**
   * Copy image to clipboard (if supported)
   */
  static async copyImage(imageData: string): Promise<boolean> {
    try {
      if (!navigator.clipboard || !window.isSecureContext) {
        return false;
      }

      // Convert base64 to blob
      const response = await fetch(imageData);
      const blob = await response.blob();
      
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob })
      ]);
      
      return true;
    } catch (error) {
      console.error('Failed to copy image:', error);
      return false;
    }
  }

  /**
   * Fallback copy method using execCommand
   */
  private static fallbackCopy(text: string): boolean {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      return successful;
    } catch (error) {
      console.error('Fallback copy failed:', error);
      return false;
    }
  }

  /**
   * Check if clipboard API is available
   */
  static isClipboardSupported(): boolean {
    return !!(navigator.clipboard && window.isSecureContext);
  }

  /**
   * Check if clipboard read is supported
   */
  static isClipboardReadSupported(): boolean {
    return this.isClipboardSupported() && typeof navigator.clipboard.readText === 'function';
  }

  /**
   * Check if clipboard write is supported
   */
  static isClipboardWriteSupported(): boolean {
    return this.isClipboardSupported() && typeof navigator.clipboard.writeText === 'function';
  }
}